import React, { useEffect, useImperativeHandle, useState ,forwardRef} from "react";
import { 
	RCCButton, 
	RCCGrid, 
	RCCTooltip,  
	RCCServicesApi,
	RCCHookToast,
	RCCIcon,
	RCCBadge, 
	arrayMove
} from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import SortableContainer from "src/components/Sortable/Container";
import SortableHandle from "src/components/Sortable/Handle";
import SortableItem from "src/components/Sortable/Item";
import Accordion from "src/components/Accordion";
import Loader from "src/components/Loader";
import { v4 } from "uuid";
import Title from "src/components/Title";
import { IndicatorLevel } from "src/components/ModalIndicator/interface";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { restrictToVerticalAxis } from "src/components/Sortable/Container/modifiers";
import { IPresentationGrouper, IPresentationIndicatorListCard, IRefPresentationGrouper } from "../interface";
import { groupPresentationBox, groupPresentationContainer, GroupPresentationTitle, StrategicIcon } from "../styles";
import { IIndicatorListCard } from "../../Hierarchy/interface";

const PresentationGrouper: React.ForwardRefRenderFunction<IRefPresentationGrouper,IPresentationGrouper> = ({
	group,
	getSelectedList,
	removeGroupFunction,
	updateGroupFunction,
	insertIndicatorCallback
},ref) => { 
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	

	const [indicatorsList,setIndicatorsList] =  useState<Array<IPresentationIndicatorListCard>>([]);
	const [totalLabel,setTotalLabel] =useState(group.indicatorsCount);
	const [loading, setLoading] = useState(false);
	const [reQuery,setReQuery] = useState(true);
	const [expandedAccordion, setExpandedAccordion] = useState(false);

	const handleAccordion = (expanded:boolean) => {
		setExpandedAccordion(expanded);

		if(!expanded || !reQuery)
			return;

		executeQuery();
	};

	const executeQuery = async() => {
		try {
			setLoading(true);
			const dataController = await RCCServicesApi.getRequest({
				method: "PresentationGroups/ListIndicators",
				urlParameters: {id:group.iqa001Id}
			});
			setIndicatorsList(dataController.data);
			setLoading(false);
			setReQuery(false);
			return dataController.data;

		} catch (error) {
			standardErrorMessages(error, translate("Erro ao buscar indicadores do grupo de apresentação"), addToast);
			setLoading(false);
			return [];
		}
	};

	const forceRequery = () => {
		setExpandedAccordion(false);
		setReQuery(true);
	};
	
	useImperativeHandle(ref,() => ({
		forceRequery
	}));

	const indicatorTitle = (indicator:IPresentationIndicatorListCard) => {
		
		switch (indicator.iqc001Nivel) 
		{
		case IndicatorLevel.STRATEGIC:
			return (
				<RCCGrid container item 
					styleInline={groupPresentationBox}>
					<GroupPresentationTitle>
						<StrategicIcon />
						<span style={{marginLeft: "0.5rem"}}>{`${indicator.iqc001Descricao} (${indicator.iqc001Codigo})`}</span>
					</GroupPresentationTitle>
					
				</RCCGrid>);
		case IndicatorLevel.TATIC:
			return (
				<RCCGrid container item 
					styleInline={groupPresentationBox}>
					<GroupPresentationTitle>
						<RCCIcon name="chessKnight" style={{marginRight: "0.5rem"}}/>
						{`${indicator.iqc001Descricao} (${indicator.iqc001Codigo})`}
					</GroupPresentationTitle>
					
				</RCCGrid>
			);
		default:
			return (
				<RCCGrid container item 
					styleInline={groupPresentationBox}>
					<GroupPresentationTitle>
						<RCCIcon name="tools" style={{marginRight: "0.5rem"}}/>
						{`${indicator.iqc001Descricao} (${indicator.iqc001Codigo})`}
					</GroupPresentationTitle>
					
				</RCCGrid>
			);
		}
	};

	const handleDragEnd = async(activeId: string, overId: string) => {
		try {
			const activeIndex = indicatorsList.findIndex((field) => field.iqa002Id.toString() === activeId);
			const overIndex = indicatorsList.findIndex((field) => field.iqa002Id.toString() === overId);
			let temp = [...indicatorsList];

			temp= arrayMove(temp, 
				activeIndex, 
				overIndex
			);

			temp = temp.map((item,index) => ({...item,iqa002Ordem:index}));
			
			await RCCServicesApi.postRequest({
				method: "PresentationGroups/UpdateOrder",
				body: temp
			});

			setIndicatorsList(temp);

		} catch (error) {
			standardErrorMessages(error, translate("Erro ao atualizar ordem do grupo de apresentação"), addToast);
		}
	};

	const insertOnGroup = async() => {
		try {
			const newIndicators = getSelectedList();
			let list: Array<IPresentationIndicatorListCard>;

			if(newIndicators.length <= 0){
				addToast({
					color: "yellow",
					description: translate("Selecione indicadores para inserir no grupo de apresentação"),
					title: translate("Aviso")
				});
				return;
			}

			if(indicatorsList.length <= 0 && reQuery){
				await executeQuery();
				list = await executeQuery();
			}else{
				list = [...indicatorsList];
			}

			const toInsertList = [] as IIndicatorListCard[];
			const repeatedIndicators = [] as IIndicatorListCard[];
			newIndicators.forEach((element) => {
				if(!list.some((x) => x.iqa002Iqc001Id === element.iqc001Id)){
					toInsertList.push(element);
				}else{
					repeatedIndicators.push(element);
				}
			});

			if(repeatedIndicators.length > 0){
				addToast({
					color: "yellow",
					description: translate("Indicadores selecionados que já estão no grupo não foram inseridos"),
					title: translate("Aviso")
				});
			}

			if(toInsertList.length <= 0){
				addToast({
					color: "yellow",
					description: translate("Não foi possivel inserir nenhum indicador"),
					title: translate("Aviso")
				});
				return;
			}

			const arrayLastPosition = list.length > 0 ? list[list.length-1].iqa002Ordem||0 : 0;

			const ordenedList = toInsertList.map((item,index) => ({ 
				...item,
				iqa002Id : 0,
				iqa002Iqa001Id : group.iqa001Id,
				iqa002Ordem : index + arrayLastPosition + 1,
				iqa002Iqc001Id: item.iqc001Id
			}));

			const dataController = await RCCServicesApi.postRequest({
				method: "PresentationGroups/InsertIndicators",
				body:ordenedList
			});

			const insertedList = dataController.data as Array<IPresentationIndicatorListCard>;
			insertIndicatorCallback();
			setIndicatorsList([...list,...insertedList]);
		} catch (error) {
			standardErrorMessages(error, translate("Erro ao inserir indicadores no grupo"), addToast);
		}
	};

	const deleteFromGroup = async(iqa002Id:number) => {
		try {
			
			await RCCServicesApi.deleteRequest({
				method: "PresentationGroups/DeleteIndicator",
				urlParameters:{id:iqa002Id}
		   });

		   addToast({
				color: "green",
				description: translate("Indicador excluído do grupo de apresentação com sucesso"),
				title: translate("Sucesso")
			});

			setIndicatorsList((prevState) => 
				prevState.filter((x) => x.iqa002Id !== iqa002Id)
			);
		} catch (error) {
			standardErrorMessages(error, translate("Erro ao excluir indicador do grupo"), addToast);
		}

	};

	useEffect(() => {
		if(!reQuery)
			setTotalLabel(indicatorsList.length);
	}, [indicatorsList]);

	return (
		<Accordion 
			expanded={expandedAccordion}
			onChange={(_, expanded) => handleAccordion(expanded)}
			TransitionProps={{ unmountOnExit: true }} 
			header={
				<>
					<RCCGrid container item 
						justifyContent="space-between" 
						alignContent="center"
						wrap="nowrap"
					>
						<RCCGrid item container 
							wrap="nowrap" 
							alignContent="center" 
							alignItems="center">
							<span>{group.iqa001Nome}</span>
							{!group.iqa001Ativo && 
								<RCCBadge color="red">
									{translate("Inativo")}
								</RCCBadge>
							}
						</RCCGrid>
						<RCCGrid container item 
							justifyContent="flex-end">
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
								<RCCTooltip
									text={translate("Inserir no grupo")}
									color="green"
								>
									<RCCButton
										icon={{name: "arrowBack", size: 12}}
										outlined
										color="green"
										layout="isRounded"
										size="xs"
										onClick={(e) => { e.stopPropagation(); insertOnGroup();}}
									/>
								</RCCTooltip>
								<RCCTooltip
									text={translate("Alterar grupo")}
									color="grey"
								>
									<RCCButton
										icon={{name: "pencil", size: 12}}
										outlined
										color="grey"
										layout="isRounded"
										size="xs"
										onClick={(e) => { e.stopPropagation(); updateGroupFunction(group);}}
									/>
								</RCCTooltip>
							</CheckPermission>
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
								<RCCTooltip
									text={translate("Excluir grupo")}
									color="red"
								>
									<RCCButton
										icon={{name: "trash", size: 12}}
										outlined
										color="red"
										layout="isRounded"
										size="xs"
										onClick={(e) => { e.stopPropagation(); removeGroupFunction(group);}}
									/>
								</RCCTooltip>
							</CheckPermission>
						</RCCGrid>
					</RCCGrid>
					<RCCGrid container item 
						justifyContent="flex-end"
					>
						<Title 
							color="purple" 
							size="sm" 
							styleInline="margin 0 4px;">
							{`${translate("Total")}:${totalLabel}`}
						</Title>
					</RCCGrid>
				</>
			}>
			{loading ? 
				<Loader/>
				:
				<RCCGrid container>
					<SortableContainer 
						items={indicatorsList}
						onDragEnd={handleDragEnd}
						modifiers={[restrictToVerticalAxis]}
					>	
					
						{indicatorsList.map((indicator) => (
							<SortableItem 
								id={indicator.iqa002Id.toString()} 
								key={v4()}
							>
								<RCCGrid container item 
									justifyContent="space-between" 
									alignContent="center"
									styleInline={groupPresentationContainer}
									wrap="nowrap"
								>
									{indicatorTitle(indicator)}
									<RCCGrid container item 
										justifyContent="flex-end">
										<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
											<SortableHandle>
												<RCCTooltip
													color="grey"
													text={translate("Ordenar")}
												>
													<RCCButton
														color="grey"
														icon={{name: "arrows", size: 12}}
														layout="isRounded"
														size="xs"
													/>
												</RCCTooltip>	
											</SortableHandle>
										</CheckPermission>
										<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
											<RCCTooltip
												text={translate("Excluir indicador")}
												color="red"
											>
												<RCCButton
													icon={{name: "trash", size: 12}}
													color="red"
													layout="isRounded"
													size="xs"
													onClick={(e) => { e.stopPropagation(); deleteFromGroup(indicator.iqa002Id);}}
												/>
											</RCCTooltip>
										</CheckPermission>
									</RCCGrid>
								</RCCGrid>
							</SortableItem>
						))
						}
					
					</SortableContainer>
				</RCCGrid>
			}
		</Accordion>
	);
};

export default forwardRef(PresentationGrouper);
